import React, { useState } from 'react';
import { useAppSelector } from 'store/hooks';

import * as api from 'api/methods';

import { COURSE_STRUCTURES, LESSON_TYPES } from 'constants/common';

import { currentCourseSelector } from 'store/course/selectors';

import { showToast } from 'helpers/showToast';

import { useFetch } from 'hooks/useFetch';

import {
  convertTheoryLessonToApi,
  convertFigmaLessonToApi,
  getLessonById,
  getFigmaLessonImages,
  getTheoryLessonImages,
} from './CopySettings.helpers';

import CopySettings from './CopySettings';
import { COURSE_TYPES } from '../../../../constants/common';

const CopySettingsContainer = ({
  onClose,
  lessonId,
  ...restProps
}: { onClose: any; lessonId: string } & Record<string, any>) => {
  const currentCourse = useAppSelector(currentCourseSelector);

  const [copyLoading, setCopyLoading] = useState(false);
  const [currentModules, setCurrentModules] = useState([]);

  const { loading: loadingCourses, data: courses } = useFetch(
    {
      defaultData: [],
      fetcher: api.getCourses,
      immediately: true,
      stopRequest: false,
    },
    { type: COURSE_TYPES.LEARNING_PATH },
    [],
  );

  const handleCourseChanged = async (courseOption: any) => {
    const nextCourse = courses.find(
      (course: any) => course.id === courseOption.value,
    );

    if (nextCourse.structure === COURSE_STRUCTURES.MODULES) {
      const modules = await api.getCourseModules(nextCourse.id);

      setCurrentModules(modules);
    }
  };

  const handleCopyClick = async (values: any) => {
    const copyLesson = getLessonById(currentCourse, lessonId);

    if (!copyLesson) {
      console.warn('not lesson found'); // TODO

      return;
    }

    try {
      setCopyLoading(true);

      switch (copyLesson.type) {
        case LESSON_TYPES.THEORY_LESSON: {
          const nextCourse = courses.find(
            (course: any) => course.id === values.course.value,
          );

          const imagesData = getTheoryLessonImages(copyLesson, nextCourse);

          await Promise.all(
            imagesData.blocksImages.map((image: any) => api.copyFile(image)),
          );

          await api.createTheoryLesson(
            convertTheoryLessonToApi(
              copyLesson,
              values.course.value,
              values.module.value,
              imagesData,
            ),
          );

          break;
        }
        case LESSON_TYPES.FIGMA_LESSON: {
          const nextCourse = courses.find(
            (course: any) => course.id === values.course.value,
          );

          const nextModule = currentModules.find(
            (module: any) => module.id === values.module.value,
          );

          const imagesData = getFigmaLessonImages(
            copyLesson,
            nextCourse,
            nextModule,
          );

          await Promise.all(
            [
              ...imagesData.canvasTheoryImages,
              imagesData.resultPreviewImage,
              ...imagesData.initialStateImages,
              ...imagesData.taskPreviewImages,
              ...(imagesData.initialStatePreviewImage
                ? [imagesData.initialStatePreviewImage]
                : []),
            ].map((image: any) => api.copyFile(image)),
          );

          await api.createFigmaLesson(
            convertFigmaLessonToApi(
              copyLesson,
              values.course.value,
              values.module.value,
              imagesData,
            ),
          );

          break;
        }
      }

      showToast({
        data: {
          variant: 'success',
          title: 'Copy lesson',
          text: 'Copy lesson is finished successfully',
        },
      });

      setCopyLoading(false);
    } catch (error) {
      console.warn(error);

      setCopyLoading(false);
    }
  };

  const loading = loadingCourses;

  const coursesOptions = !loading
    ? courses.map((course: any) => ({ label: course.title, value: course.id }))
    : [];
  const modulesOptions = !loading
    ? currentModules.map((module: any) => ({
        label: module.title,
        value: module.id,
      }))
    : [];

  return (
    <CopySettings
      {...restProps}
      loading={loading}
      copyLoading={copyLoading}
      coursesOptions={coursesOptions}
      modulesOptions={modulesOptions}
      onClose={onClose}
      onCourseChanged={handleCourseChanged}
      onCopyClick={handleCopyClick}
    />
  );
};

export default React.memo(CopySettingsContainer) as any;
